import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Cpu, HardDrive, Zap, Server, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import LeadModal from "@/components/LeadModal";

const CONFIGS = [
  {
    id: "entry-1x",
    tier: "Entry",
    label: "1× 1080p · Logo only",
    channels: "1× 1080p",
    cpu: "Intel Core i9-13900K · 24C/32T · 3.0 GHz",
    ram: "64 GB DDR5",
    gpu: "iGPU (not required)",
    storage: "1 TB NVMe primary + 1 TB media",
    io: "1× SDI in / out (Decklink Mini)",
    fps: "25p / 29.97p",
    price: "$3,900",
    use: "Single-channel logo bug playout · community TV · lobby signage",
  },
  {
    id: "pro-1x-gfx",
    tier: "Pro",
    label: "1× 1080p · Full GFX + FRC + Multi-profile HLS",
    channels: "1× 1080p",
    cpu: "AMD EPYC 7413 · 24C/48T · 2.65 GHz",
    ram: "128 GB DDR4 ECC",
    gpu: "NVIDIA RTX A4000 (16 GB)",
    storage: "2× 1 TB NVMe RAID-1 + 4 TB media",
    io: "2× SDI in / 4× SDI out (Decklink Duo 2)",
    fps: "25p / 29.97p + FRC",
    price: "$7,800",
    use: "OTT FAST channel with graphics, lower thirds, ticker, 4-profile ABR ladder",
  },
  {
    id: "pro-4x",
    tier: "Pro",
    label: "4× 1080p · Logo only + FRC",
    channels: "4× 1080p",
    cpu: "AMD EPYC 7413 · 24C/48T · 2.65 GHz",
    ram: "128 GB DDR4 ECC",
    gpu: "NVIDIA Quadro P4000 (8 GB)",
    storage: "2× 1 TB NVMe + 8 TB media",
    io: "4× SDI in / 4× SDI out (Decklink Quad 2)",
    fps: "25p / 29.97p + FRC",
    price: "$11,400",
    use: "Small MVPD headend · four regional variants of one master feed",
  },
  {
    id: "ent-6x-1080",
    tier: "Enterprise",
    label: "6× 1080p · Logo only",
    channels: "6× 1080p",
    cpu: "AMD EPYC 7543P · 32C/64T · 2.8 GHz",
    ram: "128 GB DDR4 ECC",
    gpu: "None (CPU pipeline)",
    storage: "2× 2 TB NVMe RAID-1 + 16 TB media",
    io: "8× SDI in / 8× SDI out (Decklink Duo 2 ×2)",
    fps: "25p",
    price: "$18,600",
    use: "Six-channel FAST bouquet · 24×7 news + music + weather + food + kids + movies",
  },
  {
    id: "ent-12x-720",
    tier: "Enterprise",
    label: "12× 720p · Logo only",
    channels: "12× 720p",
    cpu: "2× Intel Xeon Gold 6240 · 36C/72T · 2.6 GHz",
    ram: "192 GB DDR4 ECC",
    gpu: "2× NVIDIA RTX 4000 (8 GB ea.)",
    storage: "4× 2 TB NVMe RAID-10 + 20 TB media",
    io: "12× SDI in / 12× SDI out (Decklink Quad 2 ×3)",
    fps: "25p / 29.97p",
    price: "$28,900",
    use: "Regional MVPD headend · 12-channel FAST wall for hotel / stadium",
  },
  {
    id: "ent-2x-full-gfx",
    tier: "Enterprise",
    label: "2× 1080p · Full GFX + FRC (dual redundant)",
    channels: "2× 1080p",
    cpu: "2× Intel Xeon Silver 4214R · 24C/48T · 2.4 GHz",
    ram: "128 GB DDR4 ECC",
    gpu: "2× NVIDIA RTX A4000 (16 GB ea.)",
    storage: "2× 1 TB NVMe + 16 TB media",
    io: "4× SDI in / 8× SDI out (2× Decklink Duo 2)",
    fps: "25p / 29.97p + FRC",
    price: "$22,400",
    use: "Redundant primary+backup pair · master control room · MVPD affiliate",
  },
];

const TIER_STYLE = {
  Entry: "border-zinc-700 text-zinc-300",
  Pro: "border-emerald-500/50 text-emerald-400",
  Enterprise: "border-amber-500/50 text-amber-400",
};

const TIER_ICON = { Entry: Cpu, Pro: Zap, Enterprise: Server };

/**
 * Hardware — public prospect page (also usable inside ConsoleLayout via variant="console").
 */
export default function Hardware({ variant = "public" }) {
  const [quoteFor, setQuoteFor] = useState(null);

  const grid = (
    <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">
      {CONFIGS.map((c) => {
        const Icon = TIER_ICON[c.tier];
        return (
          <div key={c.id} data-testid={`hw-card-${c.id}`} className="panel p-4 flex flex-col">
            <div className="flex items-center justify-between mb-2">
              <span
                className={`font-mono text-[10px] uppercase tracking-widest border rounded-sm px-2 py-0.5 ${TIER_STYLE[c.tier]}`}
              >
                <Icon className="w-3 h-3 inline mr-1" /> {c.tier}
              </span>
              <span className="font-mono text-[10px] text-zinc-500">{c.fps}</span>
            </div>
            <div className="font-display font-bold text-base leading-tight">{c.label}</div>
            <div className="text-xs text-zinc-500 mt-1">{c.use}</div>

            <div className="mt-3 space-y-1 font-mono text-[11px]">
              <Row label="Channels" value={c.channels} />
              <Row label="CPU" value={c.cpu} />
              <Row label="RAM" value={c.ram} />
              <Row label="GPU" value={c.gpu} />
              <Row label="Storage" value={c.storage} icon={HardDrive} />
              <Row label="I/O" value={c.io} />
            </div>

            <div className="mt-4 flex items-center justify-between">
              <div>
                <div className="text-[9px] uppercase tracking-widest text-zinc-500">Starting at</div>
                <div className="font-display font-black text-xl">{c.price}</div>
              </div>
              <button
                data-testid={`hw-quote-${c.id}`}
                onClick={() => setQuoteFor(c)}
                className="rounded-sm px-3 py-2 tenant-btn font-mono text-[10px] uppercase tracking-widest"
              >
                Get quote
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );

  const notes = (
    <div className="mt-6 panel p-4">
      <div className="font-display font-bold mb-1">Notes</div>
      <ul className="text-xs text-zinc-400 leading-relaxed space-y-1 list-disc pl-4">
        <li>GPU is not required for logo-only playout — CPU pipeline is used with NVENC-optional software encode.</li>
        <li>Enterprise configs ship with dual PSU, IPMI, redundant NIC (4× 25 Gb SFP28), and hot-swap NVMe.</li>
        <li>Frame Rate Conversion (FRC) enables mixed 25p / 29.97p / 23.976p ingest → single-frame-rate output for OTT.</li>
        <li>All prices are hardware only; MOXYPLAY software licensing is per the Pricing page.</li>
      </ul>
    </div>
  );

  const modal = (
    <LeadModal
      open={!!quoteFor}
      onClose={() => setQuoteFor(null)}
      mode="partner-quote"
      title={quoteFor ? `Request quote · ${quoteFor.label}` : ""}
      subtitle="A MOXYPLAY hardware partner will follow up with an SO and lead time."
      context={quoteFor ? {
        hw_config_id: quoteFor.id,
        hw_config_label: quoteFor.label,
        hw_price_hint: quoteFor.price,
      } : {}}
    />
  );

  if (variant === "console") {
    return (
      <div className="p-4">
        <div className="mb-6">
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">§ Hardware</div>
          <h1 className="font-display font-bold text-2xl mt-1">Recommended playout server configurations</h1>
          <p className="text-sm text-zinc-500 mt-1">
            Certified for MOXYPLAY (based on the WisePlay 05.12.2023 hardware datasheet). All configs
            include a MOXYPLAY engine daemon license, BlackMagic Decklink drivers, and a
            NVIDIA GPU driver pre-provisioned on the shipped image.
          </p>
        </div>
        {grid}
        {notes}
        {modal}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="public-hardware-page">
      <nav className="fixed top-0 inset-x-0 z-40 border-b border-zinc-800/80 bg-[#09090B]/80 backdrop-blur">
        <div className="max-w-7xl mx-auto flex items-center justify-between h-16 px-6">
          <Link to="/" className="flex items-center gap-2" data-testid="hardware-nav-logo">
            <div className="h-8 w-8 rounded-lg overflow-hidden bg-[#060A1F] p-1 flex items-center justify-center">
              <img
                src="https://customer-assets-jt897jd0.emergentagent.net/job_broadcast-cloud-2/artifacts/jeb6uhlp_FullLogo_Transparent_NoBuffer.png"
                alt="MOXYPLAY"
                className="w-full h-full object-contain"
              />
            </div>
            <span className="font-display font-black tracking-tight text-lg">MOXYPLAY</span>
          </Link>
          <div className="hidden md:flex items-center gap-8 text-sm text-zinc-400">
            <Link to="/" className="hover:text-white" data-testid="hardware-nav-home">Home</Link>
            <Link to="/pricing" className="hover:text-white" data-testid="hardware-nav-pricing">Pricing</Link>
            <Link to="/hardware" className="text-white" data-testid="hardware-nav-hardware">Hardware</Link>
            <Link to="/login" className="hover:text-white" data-testid="hardware-nav-login">Sign in</Link>
          </div>
          <Link to="/signup" data-testid="hardware-nav-signup">
            <Button className="tenant-btn rounded-sm" size="sm">
              Start free trial <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </div>
      </nav>

      <section className="pt-32 pb-8 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">§ Hardware</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mt-2 max-w-4xl">
            Certified playout servers, ready to rack.
          </h1>
          <p className="text-zinc-400 mt-4 max-w-2xl leading-relaxed">
            Six reference configurations — from single-channel signage boxes to redundant 12-channel FAST
            headends. Every server ships with the MOXYPLAY engine daemon, BlackMagic Decklink drivers, and
            NVIDIA NVENC drivers pre-provisioned on the image.
          </p>
        </div>
      </section>

      <section className="px-6 pb-24">
        <div className="max-w-7xl mx-auto">
          {grid}
          {notes}
        </div>
      </section>

      <footer className="border-t border-zinc-900 py-8 px-6 text-center text-xs text-zinc-500 font-mono">
        © 2026 MOXYPLAY — a white-label FAST playout platform.
      </footer>

      {modal}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex gap-2">
      <span className="w-16 text-zinc-500 uppercase text-[9px] tracking-widest">{label}</span>
      <span className="text-zinc-200 flex-1">{value}</span>
    </div>
  );
}
